<script>
	import { labels, layout, clusters } from "./utils/globals";
	import { cubicOut } from "svelte/easing";
	import { analytics } from "./utils/Analytics";
	import { fade, scale } from "svelte/transition";
	import { onMount } from "svelte";
	import { data } from "./utils/data";
	import About from "./About.svelte";
	import Asset from "./Asset.svelte";
	import Intro from "./Intro.svelte";
	import Stage from "./Stage.svelte";

	let frame;
	let iteration = 0;
	let hideLabels = true;
	let hideThumbnails = true;
	let isLoaded = false;
	let isPaused = false;
	let isSimulationPaused = false;
	let selectedCluster;
	let highlightedCluster;
	let selectedItem;
	let stage;
	let showAbout = false;
	let showIntro = false;
	let w = window.innerWidth;
	let h = window.innerHeight;

	$: !isPaused && (frame = requestAnimationFrame(step));

	onMount(() => {
		resize();
		data.load({
			attributionUrl: "assets/json/attribution.json",
			dataUrl: "assets/json/data.json",
			thumbnails_url: "assets/images/atlas.jpg",
			mask_url: "assets/images/mask.png"
		}).then(_data => {
			labels.set(_data.labels);
			createClustersFrom(_data.items);
			
			//pass the preloaded texture to the thumbnails
			stage.createThumbnails(_data.texture, _data.mask);

			//fast forward the layout distribution by X steps (to get the initial state of the simulation)
			stage.precomputeLayout( 250 );

			isLoaded = true;
			showIntro = true;

			//call analytics
			analytics.introStart()
			
		});
	});

	// pause the simulation when opening a popup
	function createClustersFrom(items) {
		$labels.forEach((label, i) => {
			const clusterItems = items.filter(item => item.cluster === i);
			clusters.add(label, clusterItems);
		});
		clusters.reset();
	}

	function deselect(e) {
		isSimulationPaused = false;
		highlightedCluster = null;
		if (!e.detail) {
			selectedCluster = null;
			selectedItem = null;
			clusters.resetLinks();
		}
	}

	function getCSSColorFromCluster(cluster) {
		const color = selectedCluster ? (selectedCluster === cluster ? cluster.color : "rgba(0,0,0,.3)") : "black";
		return `color: ${color};`;
	}

	function getCSSTransformFromCluster(cluster) {
		const screenPos = stage.getScreenPositionOf(cluster.body.pos);
		// if( cluster.screenPos === undefined ){
		// 	cluster.screenPos = screenPos;
		// }
		// let p = {x:cluster.screenPos.x, y:cluster.screenPos.y}
		// p.x += (screenPos.x - p.x ) * .1
		// p.y += (screenPos.y - p.y ) * .1
		// cluster.screenPos = p;
		return `transform: translate(${screenPos.x}px, ${screenPos.y}px) translate(-50%, -50%) scale(${1 - stage.getCameraRatio(5, 50, 4) * 0.15});`;
	}

	function getCSSTransformFromItem(item) {
		const screenPos = stage.getScreenPositionOf(item.body.pos);
		return `transform: translate(${screenPos.x}px, ${screenPos.y}px) translate(-50%, -50%);`;
	}

	function getScreenPositionOf(pos) {
		return stage.getScreenPositionOf(pos);
	}

	function resize() {
		w = window.innerWidth;
		h = window.innerHeight;
		document.documentElement.style.width = w + "px";
		document.documentElement.style.height = h + "px";
		document.body.style.width = w + "px";
		document.body.style.height = h + "px";
		stage.resize();
	}

	function selectCluster(e) {
		if (e.detail.hover) {
			highlightedCluster = e.detail.cluster;
		} else {
			selectedItem = null;
			selectedCluster = e.detail.cluster;
			isSimulationPaused = false;
			clusters.clearLinks();
			selectedCluster.resetLinks();
		}
	}
	
	function selectItem(e) {
		if (e.detail.hover) {
			highlightedCluster = null;
		} else {
			selectedItem = e.detail.item;
			if (selectedCluster && selectedCluster.id !== selectedItem.cluster.id) {
				selectedCluster = null;
			}
			isSimulationPaused = true;
			const bits = selectedItem.data.GAC_url.split('/') 
			analytics.clickAsset( bits[bits.length-1] )
			// console.log(selectedItem);
		}
	}

	function showPrevItem() {
		for (let i = 0, l = selectedCluster.nodes.length; i < l; i++) {
			if (selectedCluster.nodes[i].id === selectedItem.id) {
				if (i === 0) {
					selectedItem = selectedCluster.nodes[l - 1];
				} else {
					selectedItem = selectedCluster.nodes[i - 1];
				}
				stage.focusOn(selectedItem.body.pos);
				break;
			}
		}
	}

	function showNextItem() {
		for (let i = 0, l = selectedCluster.nodes.length; i < l; i++) {
			if (selectedCluster.nodes[i].id === selectedItem.id) {
				if (i === l - 1) {
					selectedItem = selectedCluster.nodes[0];
				} else {
					selectedItem = selectedCluster.nodes[i + 1];
				}
				stage.focusOn(selectedItem.body.pos);
				break;
			}
		}
	}

	function step() {
		if (isPaused) return;
		const zoomRatio = stage.getCameraRatio(10, 15, 4)
		if (!isSimulationPaused && iteration++ % 1 == 0 ) {
			layout.simulator.settings.springLength = zoomRatio * 200;
			layout.step();
		}
		stage.step(zoomRatio);
		clusters.update(clusters => clusters);
		selectedItem && (selectedItem = selectedItem);
		frame = requestAnimationFrame(step);
	}
</script>

<svelte:window on:resize={resize}
	on:orientationchange={() => setTimeout(() => {
		resize();
	}, 500)} />

<main>
	{#if !isLoaded}
		<div class="preloader" out:fade={{ duration: 500 }}>
      <div class="orange"></div>
      <div class="blue"></div>
    </div>
	{/if}
	{#if showIntro}
		<About intro={true}
			on:reveal={() => {
				hideLabels = false;
				hideThumbnails = false;
			}}
			on:start={() => {
				hideLabels = false;
				hideThumbnails = false;
				showIntro = false;
				stage.zoomTo(window.innerWidth < 800 ? 20 : 30);
				analytics.start()
			}} />
		<!-- <Intro on:showThumbnails={() => {
				hideThumbnails = false;
			}}
			on:showLabels={() => {
				hideLabels = false;
				stage.zoomTo(30);
			}}
			on:hideAll={() => {
				hideThumbnails = true;
				hideLabels = true;
			}}
			on:showAll={() => {
				hideThumbnails = false;
				hideLabels = false;
				isPaused = false;
				isSimulationPaused = false;
			}}
			on:start={() => {
				showIntro = false;
				stage.zoomTo(30);
			}}
		/> -->
	{/if}
	<Stage hidden={hideThumbnails}
		bind:this={stage}
		on:selectCluster={selectCluster}
		on:selectItem={selectItem}
		on:deselect={deselect} />
	{#each $clusters as cluster}
		<div class="label" 
			class:hidden={hideLabels}
			class:highlight={highlightedCluster && highlightedCluster.id === cluster.id}
			class:active={selectedCluster && selectedCluster.id === cluster.id}
			style={getCSSColorFromCluster(cluster) + getCSSTransformFromCluster(cluster)}
		>{@html cluster.text.replace(", ", "<br>")}</div>
	{/each}
	{#if selectedItem}
		<div class="cache"
			transition:fade={{ duration: 500 }}
			on:click={() => stage.unfocus(true)}></div>
		<Asset data={selectedItem.data}
			offset={selectedCluster}
			position={getScreenPositionOf(selectedItem.body.pos)} />
		{#if selectedCluster}
			<div class="prev" transition:fade={{ duration: 500 }} on:click={showPrevItem}>Prev</div>
			<div class="next" transition:fade={{ duration: 500 }} on:click={showNextItem}>Next</div>
		{/if}
	{/if}
	{#if !showIntro && !showAbout && !selectedItem && isLoaded}
		<div transition:fade={{ duration: 500 }} class="background"></div>
		<a transition:fade={{ duration: 500 }} class="showGAC" href="https://artsandculture.google.com" target="_blank">
			<img src="/art-emotions-map/assets/images/GAC.svg" alt="Google Arts &amp; Culture" />
		</a>
		<div transition:fade={{ duration: 500 }} class="showAbout" on:click={() => {
			showAbout = true;
			isSimulationPaused = true;
		}}>About</div>
	{/if}
	{#if showAbout} 
		<About on:close={() => {
			showAbout = false;
			isSimulationPaused = false;
		}} />
	{/if}
	{#if showAbout || selectedItem}
		<div class="close"
			transition:fade={{ duration: 500 }}
			on:click={() => {
				if (showAbout) {
					showAbout = false;
					isSimulationPaused = false;
				} else {
					stage.unfocus(true)
					analytics.closeAsset()
				}
			}}>&times;</div>
	{/if}
</main>

<style>
	main {
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		overflow: hidden;
	}

	.preloader {
		display: flex;
		align-items: center;
		justify-content: center;
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 100;
	}

	.preloader>div {
		--animation: 700ms cubic-bezier(0.3, 0.5, 0.4, 0.9) infinite
			alternate-reverse;
		width: 2.5rem;
		height: 2.5rem;
		border-radius: 50%;
		mix-blend-mode: lighten;
	}

	.orange {
		background: linear-gradient(-35deg, #fbab7e 0%, #f7ce68 100%);
		margin-right: 2rem;
		animation: attract-orange var(--animation);
	}

	.blue {
		background: linear-gradient(35deg, #68caf4 0%, #c5f5ff 100%);
		margin-left: 2rem;
		animation: attract-blue var(--animation);
	}

	@keyframes attract-orange {
		to {
			transform: translateX(calc(2.5rem + calc(2rem / 4)));
		}
	}

	@keyframes attract-blue {
		to {
			transform: translateX(calc(2.5rem * -1 - calc(2rem / 4)));
		}
	}

	.label {
		position: absolute;
		top: 0;
		left: 0;
		text-align: center;
		user-select: none;
		pointer-events: none;
		transition: opacity 0.15s ease-in-out,
			color 0.15s ease-in-out,
			font-size 0.15s ease-in-out;
	}

	.hidden {
		opacity: 0;
	}

	.active {
		font-weight: 500;
		font-size: 1.5em;
	}

	.highlight {
		color: black !important;
		font-weight: 500;
		font-size: 1.5em;
	}

	.cache {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 10;
		/* background-color: rgba(255, 255, 255, 0.8); */
		background: radial-gradient(circle,
			hsl(0, 0%, 100%) 50%,
			hsla(0, 0%, 100%, 0.997) 54.05%,
			hsla(0, 0%, 100%, 0.99) 57.75%,
			hsla(0, 0%, 100%, 0.979) 61.25%,
			hsla(0, 0%, 100%, 0.965) 64.5%,
			hsla(0, 0%, 100%, 0.948) 67.65%,
			hsla(0, 0%, 100%, 0.93) 70.6%,
			hsla(0, 0%, 100%, 0.91) 73.55%,
			hsla(0, 0%, 100%, 0.89) 76.45%,
			hsla(0, 0%, 100%, 0.87) 79.4%,
			hsla(0, 0%, 100%, 0.852) 82.35%,
			hsla(0, 0%, 100%, 0.835) 85.5%,
			hsla(0, 0%, 100%, 0.821) 88.75%,
			hsla(0, 0%, 100%, 0.81) 92.25%,
			hsla(0, 0%, 100%, 0.803) 95.95%,
			hsla(0, 0%, 100%, 0.8) 100%
		);
		pointer-events: all;
		opacity: 0.95;
	}

	.background {
		display: none;
		width: 100%;
		height: 6rem;
		position: fixed;
		bottom: 0;
		left: 0;
		background: linear-gradient(to top, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 0));
		pointer-events: none;
	}

	.prev,
	.next {
		padding: 1rem;
		position: absolute;
		top: 50%;
		z-index: 100;
		transform: translateY(-50%);
		font-weight: 500;
		line-height: 1;
		cursor: pointer;
	}

	.prev {
		left: 2rem;
	}

	.next {
		left: auto;
		right: 2rem;
	}

	.showGAC,
	.showAbout {
		display: block;
		position: fixed;
		bottom: 1.5rem;
	}

	.showGAC {
		left: 1.5rem;
	}

	.showGAC>img {
		display: block;
		width: 13em;
    position: relative;
    top: 0.3em;
	}

	.showAbout {
		padding: 0 0.5rem;
		right: 1rem;
		font-size: 1.4em;
		font-weight: 500;
		line-height: 1;
		cursor: pointer;
	}

	.close {
		padding: 0.2rem 1rem 0.6rem;
		position: absolute;
		top: 1.5rem;
		right: 1.7rem;
		z-index: 10;
    font-size: 2.5em;
    line-height: 1;
		cursor: pointer;
	}

	@media (max-width: 960px) {
		.prev {
			left: 0.5rem;
		}

		.next {
			right: 0.5rem;
		}

		.close {
    	top: 0.7rem;
			right: 0.7rem;
		}
	}

	@media (max-width: 800px) {
		.active {
			font-size: 1.2em;
		}

		.prev,
		.next {
			top: auto;
			bottom: -1.2rem;
		}

		.background {
			display: block;
			height: 5rem;
		}

		.showGAC>img {
			width: 11em;
		}

		.showAbout {
    	padding: 0.5rem;
			bottom: 0.8rem;
			font-size: 1.2em;
		}

		/* .showAbout {
    	padding: 0.5rem 0.775rem;
    	right: 1.225rem;
    	bottom: 1rem;
			background-color: white;
	    border-radius: 100%;
   		font-size: 1.2em;
			opacity: 1;
		} */

		.close {
			top: 0.5rem;
			right: 0.4rem;
			font-size: 2em;
		}
	}

	@media (max-width: 480px) {
		.showGAC {
			bottom: 1.1rem;
			left: 1rem;
		}

		.showAbout {
			padding: 1rem;
			right: 0;
			bottom: 0;
		}

		.prev,
		.next {
			bottom: -1.5rem;
		}

		.prev {
			left: 0;
		}

		.next {
			right: 0;
		}

		.close {
    	top: 0.2rem;
			right: -0.1rem;
		}
	}
</style>